import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { TaskGroupBy, TaskStatus } from "@common/types/task";
import {
    TaskColumn,
    TaskSortValue,
    TaskView,
    TaskViewSchema,
} from "@common/types/views/tasksView";
import {
    NamedView,
    View,
    ViewDensity,
    ViewType,
} from "@common/types/views/views";
import { TaskListQuery } from "@common/contracts/task.contract";
import { FiltersTypes } from "@common/types/filters/general";
import { FiltersChangeFn } from "@components/pageHeader/filters/lib";
import { useScopedCache } from "@hooks/useScopedCache";
import { useScope } from "@state/scopeContext.lib";
import { ViewConfig } from "@lib/views";
import { NestedTaskRow } from "./types";
import { TaskListCapabilities } from "./taskListCapabilities";
import { TaskListCacheKeys } from "./taskListCacheKeys";
import { taskViewConfig } from "./taskViewConfig";
import { buildDefaultTaskColumns } from "./views/BuiltInTaskViews";
import { useTaskListBuiltInViews } from "./views/useTaskListBuiltInViews";

const CLOSE_COLUMNS = [
    TaskColumn.CLOSE,
    TaskColumn.CLOSE_STAGE,
] satisfies TaskColumn[];
const CLOSE_COLUMN_IDS = new Set<string>(CLOSE_COLUMNS);

interface UseTaskViewStateParams {
    filters: TaskListQuery;
    setFilters: (
        filters: TaskListQuery | ((prev: TaskListQuery) => TaskListQuery),
    ) => void;
    overlayFilters: FiltersTypes;
    onOverlayFiltersChange: FiltersChangeFn;
    hasCloseStages: boolean;
    hasCloses: boolean;
    capabilities: TaskListCapabilities;
    cacheKeys: TaskListCacheKeys;
    landingViewId?: string;
}

interface UseTaskViewStateReturn {
    view: TaskView;
    fullscreenView: TaskView;
    fullscreenViewForToolbar: TaskView;
    defaultFullscreenView: TaskView;
    handleFullscreenViewChange: (newView: View) => void;
    handleViewSelect: (selectedView: TaskView) => void;
    effectiveTaskViewConfig: ViewConfig<
        NestedTaskRow,
        TaskColumn,
        TaskSortValue
    >;
    builtInTaskViews: Array<NamedView<TaskView>>;
    isLoadingBuiltInTaskViews: boolean;
}

export function useTaskViewState({
    filters,
    setFilters,
    overlayFilters,
    onOverlayFiltersChange,
    hasCloseStages,
    hasCloses,
    capabilities,
    cacheKeys,
    landingViewId,
}: UseTaskViewStateParams): UseTaskViewStateReturn {
    const { organizationId } = useScope();
    const {
        builtInViews: builtInTaskViews,
        isLoadingBuiltInViews: isLoadingBuiltInTaskViews,
    } = useTaskListBuiltInViews(capabilities);
    const landingView = useMemo(
        () => builtInTaskViews.find((v) => v.id === landingViewId),
        [builtInTaskViews, landingViewId],
    );

    const {
        cachedValue: cachedFullscreenView,
        setCachedValue: setCachedFullscreenView,
    } = useScopedCache<TaskView>(cacheKeys.fullscreenView, TaskViewSchema);

    const defaultFullscreenView: TaskView = useMemo(
        () => ({
            type: ViewType.TASK,
            columns: buildDefaultTaskColumns({
                organizationId,
                hasCloseStages,
                hasCloses,
            }),
            filters: {},
            options: {
                selection: true,
                density: ViewDensity.MD,
            },
        }),
        [organizationId, hasCloseStages, hasCloses],
    );

    const [fullscreenView, setFullscreenView] = useState<TaskView>(
        cachedFullscreenView ?? landingView ?? defaultFullscreenView,
    );

    const hasPersistedLandingViewRef = useRef(false);
    useEffect(() => {
        if (!landingView || hasPersistedLandingViewRef.current) {
            return;
        }
        hasPersistedLandingViewRef.current = true;
        setCachedFullscreenView(landingView);
    }, [landingView, setCachedFullscreenView]);

    useEffect(() => {
        if (!cachedFullscreenView && !landingView) {
            setFullscreenView((prev) =>
                prev.columns === defaultFullscreenView.columns
                    ? prev
                    : { ...prev, columns: defaultFullscreenView.columns },
            );
        }
    }, [defaultFullscreenView, cachedFullscreenView, landingView]);

    // Keep the view's stored filters in sync with the current page filters
    // so that saving a view captures the active filter state.
    useEffect(() => {
        setFullscreenView((prev) => {
            const updatedFilters = {
                ...prev.filters,
                taskStatuses: filters.status?.length
                    ? filters.status
                    : undefined,
                taskSort: filters.sort ?? undefined,
                start: filters.start,
                end: filters.end,
                entityIds: filters.entityIds?.length
                    ? filters.entityIds
                    : undefined,
                taskAssigneeIds: overlayFilters.taskAssigneeIds?.length
                    ? overlayFilters.taskAssigneeIds
                    : undefined,
                taskCreatedByIds: overlayFilters.taskCreatedByIds?.length
                    ? overlayFilters.taskCreatedByIds
                    : undefined,
                isTaskUnassigned: overlayFilters.isTaskUnassigned,
                isTaskAssignedToClient: overlayFilters.isTaskAssignedToClient,
                isTaskCreatedByClient: overlayFilters.isTaskCreatedByClient,
                taskClientIds: overlayFilters.taskClientIds?.length
                    ? overlayFilters.taskClientIds
                    : undefined,
                taskCreatedAtStart: overlayFilters.taskCreatedAtStart,
                taskCreatedAtEnd: overlayFilters.taskCreatedAtEnd,
                taskCompletedByIds: overlayFilters.taskCompletedByIds?.length
                    ? overlayFilters.taskCompletedByIds
                    : undefined,
                taskCompletedAtStart: overlayFilters.taskCompletedAtStart,
                taskCompletedAtEnd: overlayFilters.taskCompletedAtEnd,
                taskCloseIds: overlayFilters.taskCloseIds?.length
                    ? overlayFilters.taskCloseIds
                    : undefined,
                taskPriorities: overlayFilters.taskPriorities?.length
                    ? overlayFilters.taskPriorities
                    : undefined,
                hasAttachments: overlayFilters.hasAttachments,
                taskGroupIds: overlayFilters.taskGroupIds?.length
                    ? overlayFilters.taskGroupIds
                    : undefined,
                hasBlockedBy: overlayFilters.hasBlockedBy,
                hasBlocking: overlayFilters.hasBlocking,
                hasRelated: overlayFilters.hasRelated,
                hasUnreadChat: overlayFilters.hasUnreadChat,
                taskCloseStageIds: overlayFilters.taskCloseStageIds?.length
                    ? overlayFilters.taskCloseStageIds
                    : undefined,
            };
            const updatedOptions = {
                ...prev.options,
                groupBy: filters.groupBy ?? null,
            };
            // Avoid spurious re-renders when nothing changed
            const filtersMatch =
                JSON.stringify(prev.filters) === JSON.stringify(updatedFilters);
            const optionsMatch =
                prev.options?.groupBy === updatedOptions.groupBy;
            if (filtersMatch && optionsMatch) {
                return prev;
            }
            return {
                ...prev,
                filters: updatedFilters,
                options: updatedOptions,
            };
        });
    }, [
        filters.status,
        filters.sort,
        filters.start,
        filters.end,
        filters.entityIds,
        filters.groupBy,
        overlayFilters.taskAssigneeIds,
        overlayFilters.taskCreatedByIds,
        overlayFilters.isTaskUnassigned,
        overlayFilters.isTaskAssignedToClient,
        overlayFilters.isTaskCreatedByClient,
        overlayFilters.taskClientIds,
        overlayFilters.taskCreatedAtStart,
        overlayFilters.taskCreatedAtEnd,
        overlayFilters.taskCompletedByIds,
        overlayFilters.taskCompletedAtStart,
        overlayFilters.taskCompletedAtEnd,
        overlayFilters.taskCloseIds,
        overlayFilters.taskPriorities,
        overlayFilters.hasAttachments,
        overlayFilters.taskGroupIds,
        overlayFilters.hasBlockedBy,
        overlayFilters.hasBlocking,
        overlayFilters.hasRelated,
        overlayFilters.hasUnreadChat,
        overlayFilters.taskCloseStageIds,
    ]);

    const handleFullscreenViewChange = useCallback(
        (newView: View) => {
            setFullscreenView(newView as TaskView);
            setCachedFullscreenView(newView as TaskView);
        },
        [setCachedFullscreenView],
    );

    // Called when a user explicitly selects a saved or built-in view.
    // Applies the view's stored filter state back to the page filters.
    const handleViewSelect = useCallback(
        (selectedView: TaskView) => {
            setFullscreenView(selectedView);
            setCachedFullscreenView(selectedView);
            setFilters((prev) => ({
                ...prev,
                status: selectedView.filters.taskStatuses as
                    | TaskStatus[]
                    | undefined,
                sort: selectedView.filters.taskSort as
                    | TaskSortValue
                    | undefined,
                groupBy:
                    (selectedView.options?.groupBy as TaskGroupBy) ??
                    TaskGroupBy.STATUS,
                start: selectedView.filters.start,
                end: selectedView.filters.end,
                entityIds: selectedView.filters.entityIds,
            }));
            onOverlayFiltersChange({
                taskAssigneeIds: selectedView.filters.taskAssigneeIds,
                isTaskUnassigned: selectedView.filters.isTaskUnassigned,
                isTaskAssignedToClient: capabilities.canFilterByClientAssignee
                    ? selectedView.filters.isTaskAssignedToClient
                    : undefined,
                taskCreatedByIds: selectedView.filters.taskCreatedByIds,
                isTaskCreatedByClient: capabilities.canFilterByClientCreator
                    ? selectedView.filters.isTaskCreatedByClient
                    : undefined,
                taskClientIds: capabilities.canFilterByClient
                    ? selectedView.filters.taskClientIds
                    : undefined,
                taskCreatedAtStart: selectedView.filters.taskCreatedAtStart,
                taskCreatedAtEnd: selectedView.filters.taskCreatedAtEnd,
                taskCompletedByIds: selectedView.filters.taskCompletedByIds,
                taskCompletedAtStart: selectedView.filters.taskCompletedAtStart,
                taskCompletedAtEnd: selectedView.filters.taskCompletedAtEnd,
                taskCloseIds: selectedView.filters.taskCloseIds,
                taskPriorities: selectedView.filters.taskPriorities,
                hasAttachments: selectedView.filters.hasAttachments,
                taskGroupIds: selectedView.filters.taskGroupIds,
                hasBlockedBy: selectedView.filters.hasBlockedBy,
                hasBlocking: selectedView.filters.hasBlocking,
                hasRelated: selectedView.filters.hasRelated,
                hasUnreadChat: selectedView.filters.hasUnreadChat,
                taskCloseStageIds: selectedView.filters.taskCloseStageIds,
            });
        },
        [
            setFilters,
            setCachedFullscreenView,
            onOverlayFiltersChange,
            capabilities.canFilterByClientCreator,
            capabilities.canFilterByClientAssignee,
            capabilities.canFilterByClient,
        ],
    );

    const effectiveTaskViewConfig: ViewConfig<
        NestedTaskRow,
        TaskColumn,
        TaskSortValue
    > = useMemo(() => {
        if (organizationId) {
            return taskViewConfig;
        }
        const hiddenColumns = Object.fromEntries(
            CLOSE_COLUMNS.map((colId) => [
                colId,
                {
                    ...taskViewConfig.columns[colId],
                    hiddenFromSettings: true,
                    internal: true,
                },
            ]),
        );
        return {
            ...taskViewConfig,
            columns: { ...taskViewConfig.columns, ...hiddenColumns },
        };
    }, [organizationId]);

    const fullscreenViewForToolbar = useMemo(() => {
        if (organizationId) {
            return fullscreenView;
        }
        return {
            ...fullscreenView,
            columns: fullscreenView.columns.filter(
                (col) => !CLOSE_COLUMN_IDS.has(col.id),
            ),
        };
    }, [fullscreenView, organizationId]);

    const view = useMemo(() => {
        const baseView = {
            ...fullscreenView,
            options: { ...fullscreenView.options, bulkActions: true },
        };

        const columns: TaskView["columns"] = baseView.columns.filter(
            (col) => col.id !== TaskColumn.CLIENT,
        );
        if (capabilities.isCrossWorkspace) {
            const nameIndex = columns.findIndex(
                (col) => col.id === TaskColumn.NAME,
            );
            columns.splice(nameIndex + 1, 0, { id: TaskColumn.CLIENT });
        }

        return {
            ...baseView,
            columns: organizationId
                ? columns
                : columns.filter((col) => !CLOSE_COLUMN_IDS.has(col.id)),
        };
    }, [fullscreenView, organizationId, capabilities.isCrossWorkspace]);

    return {
        view,
        fullscreenView,
        fullscreenViewForToolbar,
        defaultFullscreenView,
        handleFullscreenViewChange,
        handleViewSelect,
        effectiveTaskViewConfig,
        builtInTaskViews,
        isLoadingBuiltInTaskViews,
    };
}
